class ProductCompare extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['table', 'column', 'progress', 'progressbar']
  }

  connectedCallback () {
    if (this.hasBeenInitialised) return

    super.connectedCallback()

    this.updateProgressState()
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    this.resizeObserver?.disconnect()
  }

  setupListeners () {
    this.table = this.els.table.element
    this.column = this.els.column.element
    this.progress = this.els.progress.element
    this.progressbar = this.els.progressbar.element
    this.next = this.els.next?.element
    this.previous = this.els.previous?.element

    this.listeners.add(
      this.table,
      'scroll',
      () => this.updateProgressState(),
      { passive: true },
    )

    this.resizeObserver = new ResizeObserver(() => this.updateProgressState())
    this.resizeObserver.observe(this.table)

    if (this.next) {
      this.listeners.add(this.next, 'click', () => this.scrollByColumn(1))
    }

    if (this.previous) {
      this.listeners.add(this.previous, 'click', () => this.scrollByColumn(-1))
    }

    this.setupDragToScroll()
  }

  scrollByColumn (direction) {
    const amount = this.column.getBoundingClientRect().width

    this.table.scrollBy({ left: direction * amount, behavior: 'smooth' })
  }

  setupDragToScroll () {
    this.dragState = null

    this.listeners.add(this.table, 'pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.table.scrollLeft,
        hasDragged: false,
        lastX: event.clientX,
        lastTime: event.timeStamp,
        velocity: 0,
      }
    })

    this.listeners.add(this.table, 'pointermove', (event) => {
      if (this.dragState?.pointerId !== event.pointerId) return

      const distance = event.clientX - this.dragState.startX

      if (!this.dragState.hasDragged && Math.abs(distance) < 5) return

      if (!this.dragState.hasDragged) {
        this.dragState.hasDragged = true
        this.table.setPointerCapture(event.pointerId)
      }

      this.table.scrollLeft = this.dragState.startScrollLeft - distance

      const elapsed = event.timeStamp - this.dragState.lastTime

      if (elapsed > 0) {
        this.dragState.velocity =
          (this.dragState.lastX - event.clientX) / elapsed
        this.dragState.lastX = event.clientX
        this.dragState.lastTime = event.timeStamp
      }
    })

    const endDrag = (event) => {
      if (this.dragState?.pointerId !== event.pointerId) return

      if (this.dragState.hasDragged) {
        this.table.releasePointerCapture(event.pointerId)
        this.suppressNextClick = true
        this.applyMomentum(this.dragState.velocity)
      }

      this.dragState = null
    }

    this.listeners.add(this.table, 'pointerup', endDrag)
    this.listeners.add(this.table, 'pointercancel', endDrag)

    this.listeners.add(this.table, 'click', (event) => {
      if (!this.suppressNextClick) return

      this.suppressNextClick = false

      event.preventDefault()
      event.stopPropagation()
    })
  }

  applyMomentum (velocity) {
    const distance = velocity * ProductCompare.MOMENTUM_MULTIPLIER

    if (Math.abs(distance) < ProductCompare.MOMENTUM_THRESHOLD) return

    const clamped = Math.max(
      -ProductCompare.MOMENTUM_MAX_DISTANCE,
      Math.min(ProductCompare.MOMENTUM_MAX_DISTANCE, distance),
    )

    this.table.scrollBy({ left: clamped, behavior: 'smooth' })
  }

  updateProgressState () {
    this.style.setProperty(
      '--Compare_StickyWidth',
      `${this.column.getBoundingClientRect().width}px`,
    )

    this.updateControlState()

    const maximumScroll = this.table.scrollWidth - this.table.clientWidth

    this.progress.setAttribute('aria-hidden', maximumScroll <= 0)

    if (maximumScroll <= 0) return

    const barLength = (this.table.clientWidth / this.table.scrollWidth) * 100
    const barPosition = (this.table.scrollLeft / maximumScroll) * (100 - barLength)

    this.progressbar.style.setProperty('--Bar_Width', `${barLength}%`)
    this.progressbar.style.setProperty('--Bar_Position', `${barPosition}%`)
  }

  updateControlState () {
    const maximumScroll = this.table.scrollWidth - this.table.clientWidth
    const canScrollPrevious = maximumScroll > 0 && this.table.scrollLeft > 1
    const canScrollNext =
      maximumScroll > 0 && this.table.scrollLeft < maximumScroll - 1

    this.setAttribute('data-can-scroll-previous', canScrollPrevious)
    this.setAttribute('data-can-scroll-next', canScrollNext)

    if (this.previous) this.previous.disabled = !canScrollPrevious
    if (this.next) this.next.disabled = !canScrollNext
  }

  static get MOMENTUM_MULTIPLIER () {
    return 120
  }

  static get MOMENTUM_THRESHOLD () {
    return 20
  }

  static get MOMENTUM_MAX_DISTANCE () {
    return 1200
  }
}

if (!customElements.get('product-compare')) {
  customElements.define('product-compare', ProductCompare)
}
